// Accommodation: providers, properties, rooms and bookings (with per-person allocations).
import React, { useEffect, useState } from 'react';
import { api, saveWithOverride } from '../api';
import { RegisterPage, FieldSpec, useLookup, dateCol, statusCol, boolCol } from '../crud';
import { Tabs } from '../ui';
import { toAus, money, BOOKING_STATUSES, diffDays } from '../util';

export default function Accommodation() {
  const [tab, setTab] = useState('bookings');
  return (
    <div className="page">
      <Tabs active={tab} onSelect={setTab} tabs={[
        { key: 'bookings', label: 'Bookings' },
        { key: 'providers', label: 'Providers' },
        { key: 'properties', label: 'Properties' },
        { key: 'rooms', label: 'Rooms' }
      ]} />
      {tab === 'providers' && <Providers />}
      {tab === 'properties' && <Properties />}
      {tab === 'rooms' && <Rooms />}
      {tab === 'bookings' && <Bookings />}
    </div>
  );
}

function Providers() {
  const specs: FieldSpec[] = [
    { name: 'name', label: 'Provider name', type: 'text', required: true },
    { name: 'contact_person', label: 'Contact person', type: 'text' },
    { name: 'phone', label: 'Phone', type: 'text' },
    { name: 'email', label: 'Email', type: 'email' },
    { name: 'booking_ref_format', label: 'Booking reference format', type: 'text' },
    { name: 'cancellation_notice_days', label: 'Cancellation notice (days)', type: 'int', default: 14 },
    { name: 'booking_lead_days', label: 'Booking lead time (days)', type: 'int', default: 60 },
    { name: 'standard_rate', label: 'Standard room rate ($/night)', type: 'real' },
    { name: 'active', label: 'Active', type: 'bool', default: 1 },
    { name: 'notes', label: 'Notes', type: 'textarea' }
  ];
  return <RegisterPage title="Accommodation providers" entity="providers" specs={specs} columns={[
    { key: 'name', label: 'Provider', render: r => <strong>{r.name}</strong> },
    { key: 'contact_person', label: 'Contact' },
    { key: 'phone', label: 'Phone' },
    { key: 'booking_lead_days', label: 'Lead (days)' },
    { key: 'cancellation_notice_days', label: 'Cancel notice (days)' },
    { key: 'standard_rate', label: 'Rate', render: r => money(r.standard_rate), sortValue: r => r.standard_rate },
    boolCol('active', 'Active')
  ]} />;
}

function Properties() {
  const providers = useLookup('providers');
  const specs: FieldSpec[] = [
    { name: 'provider_id', label: 'Provider', type: 'select', lookup: 'providers', required: true },
    { name: 'name', label: 'Property name', type: 'text', required: true },
    { name: 'address', label: 'Address', type: 'text' },
    { name: 'active', label: 'Active', type: 'bool', default: 1 },
    { name: 'notes', label: 'Notes', type: 'textarea' }
  ];
  return <RegisterPage title="Properties" entity="properties" specs={specs} lookupEntities={{ providers }} columns={[
    { key: 'name', label: 'Property', render: r => <strong>{r.name}</strong> },
    { key: 'provider_id', label: 'Provider', render: r => providers.byId[r.provider_id] || '', sortValue: r => providers.byId[r.provider_id] },
    { key: 'address', label: 'Address' },
    boolCol('active', 'Active')
  ]} />;
}

function Rooms() {
  const properties = useLookup('properties');
  const specs: FieldSpec[] = [
    { name: 'property_id', label: 'Property', type: 'select', lookup: 'properties', required: true },
    { name: 'room_number', label: 'Room number / name', type: 'text', required: true },
    { name: 'room_type', label: 'Room type', type: 'text', default: 'Single' },
    { name: 'max_occupants', label: 'Max occupants', type: 'int', default: 1 },
    { name: 'shared', label: 'Shared room', type: 'bool' },
    { name: 'ensuite', label: 'Ensuite', type: 'bool', default: 1 },
    { name: 'kitchen', label: 'Kitchen facilities', type: 'bool' },
    { name: 'active', label: 'Active', type: 'bool', default: 1 },
    { name: 'notes', label: 'Notes', type: 'textarea' }
  ];
  return <RegisterPage title="Rooms" entity="rooms" specs={specs} lookupEntities={{ properties }} columns={[
    { key: 'room_number', label: 'Room', render: r => <strong>{r.room_number}</strong> },
    { key: 'property_id', label: 'Property', render: r => properties.byId[r.property_id] || '', sortValue: r => properties.byId[r.property_id] },
    { key: 'room_type', label: 'Type' },
    { key: 'max_occupants', label: 'Max occ.' },
    boolCol('ensuite', 'Ensuite'),
    boolCol('shared', 'Shared'),
    boolCol('active', 'Active')
  ]} />;
}

function Bookings() {
  const providers = useLookup('providers');
  const properties = useLookup('properties');
  const projects = useLookup('projects');
  const specs: FieldSpec[] = [
    { name: 'provider_id', label: 'Provider', type: 'select', lookup: 'providers', required: true, section: 'Booking' },
    { name: 'property_id', label: 'Property', type: 'select', lookup: 'properties', section: 'Booking' },
    { name: 'project_id', label: 'Project', type: 'select', lookup: 'projects', section: 'Booking' },
    { name: 'booking_reference', label: 'Booking reference', type: 'text', section: 'Booking' },
    { name: 'status', label: 'Status', type: 'select', options: BOOKING_STATUSES, default: 'forecast', section: 'Booking' },
    { name: 'check_in', label: 'Check-in', type: 'date', required: true, section: 'Dates & rooms' },
    { name: 'check_out', label: 'Check-out', type: 'date', required: true, section: 'Dates & rooms' },
    { name: 'num_rooms', label: 'Number of rooms', type: 'int', default: 1, section: 'Dates & rooms' },
    { name: 'room_type', label: 'Room type', type: 'text', section: 'Dates & rooms' },
    { name: 'date_requested', label: 'Date requested', type: 'date', section: 'Dates & rooms' },
    { name: 'date_confirmed', label: 'Date confirmed', type: 'date', section: 'Dates & rooms' },
    { name: 'cancellation_deadline', label: 'Cancellation deadline', type: 'date', section: 'Dates & rooms' },
    { name: 'nightly_rate', label: 'Nightly rate ($)', type: 'real', section: 'Commercial' },
    { name: 'po_number', label: 'Purchase order no.', type: 'text', section: 'Commercial' },
    { name: 'invoice_reference', label: 'Invoice reference', type: 'text', section: 'Commercial' },
    { name: 'contact_person', label: 'Contact person', type: 'text', section: 'Commercial' },
    { name: 'attachment_ref', label: 'Attachment / document reference', type: 'text', section: 'Commercial' },
    { name: 'notes', label: 'Notes', type: 'textarea', section: 'Commercial' }
  ];
  return (
    <RegisterPage
      title="Accommodation bookings"
      entity="bookings"
      specs={specs}
      lookupEntities={{ providers, properties, projects }}
      columns={[
        { key: 'booking_reference', label: 'Reference', render: r => <strong>{r.booking_reference || `#${r.id}`}</strong> },
        { key: 'provider_id', label: 'Provider', render: r => providers.byId[r.provider_id] || '', sortValue: r => providers.byId[r.provider_id] },
        statusCol(),
        dateCol('check_in', 'Check-in'),
        dateCol('check_out', 'Check-out'),
        { key: 'num_rooms', label: 'Rooms' },
        { key: 'est', label: 'Est. cost', render: r => r.nightly_rate != null ? money(diffDays(r.check_in, r.check_out) * r.num_rooms * r.nightly_rate) : '', sortValue: r => r.nightly_rate },
        dateCol('cancellation_deadline', 'Cancel by'),
        { key: 'po_number', label: 'PO' }
      ]}
      onRowExtra={booking => <AllocationEditor booking={booking} />}
    />
  );
}

function AllocationEditor({ booking }: { booking: any }) {
  const [rows, setRows] = useState<any[]>([]);
  const people = useLookup('people');
  const rooms = useLookup('rooms', r => r.room_number);
  const [personId, setPersonId] = useState('');
  const [roomId, setRoomId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const load = () => api(`/allocations?booking_id=${booking.id}`).then(setRows).catch(() => {});
  useEffect(() => { load(); }, [booking.id]);

  const add = async () => {
    setError(null);
    try {
      await saveWithOverride('/allocations', 'POST', {
        booking_id: booking.id, person_id: Number(personId), room_id: roomId ? Number(roomId) : null
      });
      setPersonId(''); setRoomId('');
      load();
    } catch (e: any) { if (e.status !== 0) setError(e.message); }
  };

  return (
    <div style={{ marginTop: 16 }}>
      <h4 className="form-section">Assigned personnel ({rows.length} of {booking.num_rooms} rooms allocated)</h4>
      {error && <div className="error-box">{error}</div>}
      {rows.length === 0 && <p className="muted">Block booking — no named allocations yet. Rooms can be booked before names are confirmed.</p>}
      {rows.map(r => (
        <div key={r.id} className="btn-row" style={{ marginBottom: 4, fontSize: 13 }}>
          <span><strong>{people.byId[r.person_id] || r.person_id}</strong></span>
          <span className="muted">{r.room_id ? `Room ${rooms.byId[r.room_id] || r.room_id}` : 'no specific room'}</span>
          <input className="input" style={{ width: 150 }} placeholder="Confirmation no."
            defaultValue={r.confirmation_no ?? ''}
            onBlur={async e => {
              if (e.target.value === (r.confirmation_no ?? '')) return;
              try { await api(`/allocations/${r.id}`, { method: 'PUT', body: { confirmation_no: e.target.value } }); load(); }
              catch (err: any) { setError(err.message); }
            }} />
          {(r.start_date || r.end_date) && <span className="muted">{toAus(r.start_date)} → {toAus(r.end_date)}</span>}
          <button className="btn btn-ghost btn-sm" onClick={async () => { await api(`/allocations/${r.id}`, { method: 'DELETE' }); load(); }}>remove</button>
        </div>
      ))}
      <div className="btn-row" style={{ marginTop: 8 }}>
        <select className="input" value={personId} onChange={e => setPersonId(e.target.value)}>
          <option value="">Assign person…</option>
          {people.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select className="input" value={roomId} onChange={e => setRoomId(e.target.value)}>
          <option value="">Any room / unnamed</option>
          {rooms.options.map(o => <option key={o.value} value={o.value}>Room {o.label}</option>)}
        </select>
        <button className="btn btn-sm" onClick={add} disabled={!personId}>Assign</button>
      </div>
    </div>
  );
}
